import React from 'react';
import { Link } from 'react-router-dom';
import { GradientTitle } from '../components/ui';
import { Helmet } from 'react-helmet-async';
import type { CognitiveBias } from '../types';

interface SitemapPageProps {
  biases: CognitiveBias[];
}

// Static bias data for prerendering
const staticBiases = {
  "Decision-Making": [
    { id: "bias-anchoring", name: "Anchoring Bias" },
    { id: "bias-survivorship", name: "Survivorship Bias" },
    { id: "bias-confirmation", name: "Confirmation Bias" },
    { id: "bias-availability", name: "Availability Heuristic" },
    { id: "bias-sunk-cost", name: "Sunk Cost Fallacy" },
    { id: "bias-status-quo", name: "Status Quo Bias" }
  ],
  "Social": [
    { id: "bias-self-serving", name: "Self-Serving Bias" },
    { id: "bias-in-group", name: "In-Group Bias" },
    { id: "bias-false-consensus", name: "False Consensus Effect" },
    { id: "bias-halo-effect", name: "Halo Effect" },
    { id: "bias-fundamental-attribution", name: "Fundamental Attribution Error" },
    { id: "bias-bandwagon", name: "Bandwagon Effect" }
  ],
  "Memory": [
    { id: "bias-recency-effect", name: "Recency Effect" },
    { id: "bias-hindsight", name: "Hindsight Bias" },
    { id: "bias-false-memory", name: "False Memory Bias" },
    { id: "bias-misinformation", name: "Misinformation Effect" },
    { id: "bias-rosy-retrospection", name: "Rosy Retrospection" },
    { id: "bias-context-effect", name: "Context Effect" }
  ],
  "Reasoning": [
    { id: "bias-planning-fallacy", name: "Planning Fallacy" },
    { id: "bias-gamblers-fallacy", name: "Gambler's Fallacy" },
    { id: "bias-base-rate", name: "Base Rate Fallacy" },
    { id: "bias-belief", name: "Belief Bias" },
    { id: "bias-dunning-kruger", name: "Dunning-Kruger Effect" },
    { id: "bias-backfire-effect", name: "Backfire Effect" }
  ],
  "Perceptual": [
    { id: "bias-illusory-correlation", name: "Illusory Correlation" },
    { id: "bias-negativity", name: "Negativity Bias" },
    { id: "bias-attentional", name: "Attentional Bias" },
    { id: "bias-framing", name: "Framing Effect" },
    { id: "bias-selective-perception", name: "Selective Perception" },
    { id: "bias-blindspot", name: "Blind Spot Bias" }
  ]
};

export const SitemapPage: React.FC<SitemapPageProps> = ({ biases = [] }) => {
  // Group biases by category from the dynamic data if available
  const biasesByCategory = (biases || []).reduce((acc, bias) => {
    if (!bias || !bias.category) return acc;
    
    if (!acc[bias.category]) {
      acc[bias.category] = [];
    }
    
    acc[bias.category].push(bias);
    return acc;
  }, {} as Record<string, CognitiveBias[]>);

  // Use dynamic data if available, otherwise fall back to static data
  const hasValidDynamicData = Object.keys(biasesByCategory).length > 0;

  return (
    <>
      <Helmet prioritizeSeoTags>
        <title>Sitemap | Cognitive Bias Lab</title>
        <meta 
          name="description" 
          content="Complete sitemap of Cognitive Bias Lab. Find links to all cognitive bias pages, categories, and other resources." 
        />
        <link 
          rel="canonical" 
          href="https://www.cognitivebiaslab.com/sitemap-page/"
          key="canonical"
          data-source="page-component"
        />
        
        {/* OpenGraph tags for better social sharing */}
        <meta property="og:title" content="Sitemap | Cognitive Bias Lab" />
        <meta 
          property="og:description" 
          content="Complete sitemap of Cognitive Bias Lab. Find links to all cognitive bias pages, categories, and other resources."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.cognitivebiaslab.com/sitemap-page/" />
        <meta property="og:site_name" content="Cognitive Bias Lab" />
        <meta property="og:image" content="https://www.cognitivebiaslab.com/logo.webp" />
        <meta property="og:image:width" content="512" />
        <meta property="og:image:height" content="512" />
        <meta property="og:image:type" content="image/webp" />
        
        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Sitemap | Cognitive Bias Lab" />
        <meta 
          name="twitter:description" 
          content="Complete sitemap of Cognitive Bias Lab. Find links to all cognitive bias pages, categories, and other resources."
        />
        <meta name="twitter:image" content="https://www.cognitivebiaslab.com/logo.webp" />
      </Helmet>

      <div className="min-h-screen bg-orange-50 py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <GradientTitle className="text-3xl md:text-4xl mb-4" useH1={true}>
                Sitemap
              </GradientTitle>
              <p className="text-lg text-gray-600">
                A complete list of all pages on Cognitive Bias Lab
              </p>
            </div>

            <div className="bg-white/90 backdrop-blur-sm p-8 rounded-3xl shadow-lg border border-gray-100 mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-gray-800">Main Pages</h2>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <li><Link to="/" className="text-orange-600 hover:text-orange-700 hover:underline">Home</Link></li>
                <li><Link to="/learn/" className="text-orange-600 hover:text-orange-700 hover:underline">Learn</Link></li>
                <li><Link to="/practice/" className="text-orange-600 hover:text-orange-700 hover:underline">Practice</Link></li>
                <li><Link to="/practice/test/" className="text-orange-600 hover:text-orange-700 hover:underline">Practice Tests</Link></li>
                <li><Link to="/practice/flipcards/" className="text-orange-600 hover:text-orange-700 hover:underline">Flashcards</Link></li>
                <li><Link to="/practice/match/" className="text-orange-600 hover:text-orange-700 hover:underline">Matching Game</Link></li>
                <li><Link to="/quiz/" className="text-orange-600 hover:text-orange-700 hover:underline">Quiz</Link></li>
                <li><Link to="/network/" className="text-orange-600 hover:text-orange-700 hover:underline">Network Visualization</Link></li>
                <li><Link to="/pre-mortem/" className="text-orange-600 hover:text-orange-700 hover:underline">Pre-Mortem Analysis Tool</Link></li>
                <li><Link to="/blog/" className="text-orange-600 hover:text-orange-700 hover:underline">Blog</Link></li>
                <li><Link to="/about/" className="text-orange-600 hover:text-orange-700 hover:underline">About</Link></li>
                <li><Link to="/contact/" className="text-orange-600 hover:text-orange-700 hover:underline">Contact</Link></li>
                <li><Link to="/privacy/" className="text-orange-600 hover:text-orange-700 hover:underline">Privacy Policy</Link></li>
                <li><Link to="/terms/" className="text-orange-600 hover:text-orange-700 hover:underline">Terms of Service</Link></li>
                <li><Link to="/biases/" className="text-orange-600 hover:text-orange-700 hover:underline">All Biases</Link></li>
                <li><Link to="/support/" className="text-orange-600 hover:text-orange-700 hover:underline">Support</Link></li>
                <li><Link to="/practice/ai-debiasing-roleplay/" className="text-orange-600 hover:text-orange-700 hover:underline">AI Role-Play Debiasing</Link></li>
              </ul>
            </div>

            <div className="bg-white/90 backdrop-blur-sm p-8 rounded-3xl shadow-lg border border-gray-100 mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-gray-800">Categories</h2>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <li><Link to="/category/memory/" className="text-orange-600 hover:text-orange-700 hover:underline">Memory Biases</Link></li>
                <li><Link to="/category/social/" className="text-orange-600 hover:text-orange-700 hover:underline">Social Biases</Link></li>
                <li><Link to="/category/decision-making/" className="text-orange-600 hover:text-orange-700 hover:underline">Decision-Making Biases</Link></li>
                <li><Link to="/category/perceptual/" className="text-orange-600 hover:text-orange-700 hover:underline">Perceptual Biases</Link></li>
                <li><Link to="/category/reasoning/" className="text-orange-600 hover:text-orange-700 hover:underline">Reasoning Biases</Link></li>
              </ul>
            </div>

            {/* Render bias categories - use dynamic data if available, otherwise use static data */}
            {hasValidDynamicData ? (
              // Render from dynamic data
              Object.entries(biasesByCategory).map(([category, categoryBiases]) => (
                <div key={category} className="bg-white/90 backdrop-blur-sm p-8 rounded-3xl shadow-lg border border-gray-100 mb-8">
                  <h2 className="text-2xl font-semibold mb-4 text-gray-800">{category} Biases</h2>
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {categoryBiases.map(bias => (
                      <li key={bias.id}>
                        <Link 
                          to={`/bias/${bias.id}/`} 
                          className="text-orange-600 hover:text-orange-700 hover:underline"
                        >
                          {bias.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))
            ) : (
              // Render from static data
              Object.entries(staticBiases).map(([category, biases]) => (
                <div key={category} className="bg-white/90 backdrop-blur-sm p-8 rounded-3xl shadow-lg border border-gray-100 mb-8">
                  <h2 className="text-2xl font-semibold mb-4 text-gray-800">{category} Biases</h2>
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {biases.map(bias => (
                      <li key={bias.id}>
                        <Link 
                          to={`/bias/${bias.id}/`} 
                          className="text-orange-600 hover:text-orange-700 hover:underline"
                        >
                          {bias.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default SitemapPage; 